import React, { useState } from 'react';
import { Button } from './ui/button';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import ChampAntiRobot from './ChampAntiRobot';
import { trackFormSubmit } from '../utils/analytics';
import { attributionCourante } from "../utils/attribution";

const ContactForm = ({ 
  type = "general", 
  title = "Contactez-nous", 
  description = "Remplissez ce formulaire et nous vous recontacterons rapidement",
  onClose = null 
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    message: '',
    type_demande: type,
    website: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validation frontend
    if (!formData.nom || !formData.email || !formData.telephone || !formData.message) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Tous les champs sont obligatoires.' 
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/send-general-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, attribution: attributionCourante() })
      });

      const result = await response.json();
      
      // Debug: log the response for troubleshooting
      console.log('Contact form response:', { status: response.status, result });

      if (result.success) {
        trackFormSubmit('contact');
        setSubmitStatus({ type: 'success', message: result.message });
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          message: '',
          type_demande: type || "general",
          website: ''
        });
        
        // Close modal after 2 seconds if it's a modal
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      } else {
        setSubmitStatus({ type: 'error', message: result.message });
      }
    } catch (error) {
      console.error('Erreur envoi formulaire:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Erreur de connexion. Veuillez réessayer ou nous appeler au 09 39 03 54 44.' 
      });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-700">{description}</p>
      </div>

      {/* Status Messages */}
      {submitStatus && (
        <div className={`mb-6 p-4 rounded-lg ${
          submitStatus.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {submitStatus.type === 'success' ? (
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            <span className="text-sm font-medium">{submitStatus.message}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <ChampAntiRobot
          id="website-contactform"
          valeur={formData.website}
          onChange={handleInputChange}
        />
        <div>
          <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
            Nom complet *
          </label>
          <input
            type="text"
            id="nom"
            name="nom"
            required
            value={formData.nom}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Votre nom et prénom"
          />
        </div>

        <div>
          <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-1">
            Téléphone *
          </label>
          <input
            type="tel"
            id="telephone"
            name="telephone"
            required
            value={formData.telephone}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="06 12 34 56 78"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="votre@email.fr"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Votre message *
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows="4"
            value={formData.message}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-vertical"
            placeholder={
              type === 'rappel' 
                ? "Indiquez vos disponibilités pour être rappelé(e)..."
                : type === 'conseil'
                ? "Décrivez votre projet d'adaptation..."
                : "Décrivez votre demande..."
            }
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-orange-700 hover:bg-orange-800 text-white font-medium py-3 shadow-lg disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Envoi en cours...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Envoyer ma demande
              </>
            )}
          </Button>
          
          {onClose && (
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="sm:w-auto border-gray-300 hover:bg-gray-50"
            >
              Fermer
            </Button>
          )}
        </div>

        {/* Quick Contact */}
        <div className="mt-6 pt-6 border-t border-gray-300">
          <p className="text-center text-sm text-gray-700 mb-3">
            Besoin d'une réponse immédiate ?
          </p>
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.location.href = 'tel:0939035444'}
              className="border-orange-200 text-orange-700 hover:bg-orange-50"
            >
              <Phone className="h-4 w-4 mr-2" />
              Appeler maintenant : 09 39 03 54 44
            </Button>
          </div>
        </div>
		        {/* RGPD Notice */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Les informations recueillies via ce formulaire sont utilisées uniquement pour répondre à votre demande.
          Pour en savoir plus sur la gestion de vos données, consultez notre{' '}
          <a href="/politique-confidentialite" className="text-orange-700 underline hover:text-orange-800">
            Politique de confidentialité
          </a>.
        </p>
      </form>
    </div>
  );
};

export default ContactForm;